import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor } from "@tiptap/react";
import { EditorState } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import { useParams } from "react-router-dom";
import { exportView } from "../../navigation";
import { SegFlags, translate } from "../../common/providers";
import styles from "./Notes.module.css";
import { CentralPanel, DocumentViewer, EmptyState } from "./components";
import NotesSidePanel from "./components/notes-side-panel/NotesSidePanel";
import { CustomIconButton, icon, IconComponent } from "../../common/components";
import {
	ContentBoxExtention,
	LineReferenceExtention,
} from "./components/right-side-panel/components/rich-text-editor/components/rich-text-editor/extensions";
import { useApi, usePrevious, useTrackEvent } from "../../common/hooks";
import { connectSSE, NotesService, TransverseService } from "../../api";
import { debounce, debounceWithMaxWait, isDeepEqual, isNonEmptyArray, isNonEmptyObject } from "../../common/utils";
import {
	setIsEditable,
	setIsGenerating,
	setIsLoading,
	setNewlyGeneratedNote,
	setIsSaving,
	setSelectedNote,
} from "./slice/note/note-slice";
import { OPERATORS } from "../../common/constants";
import { setSelectedDocument } from "./slice/notes-selected-document/notes-selected-document-slice";
import { NotesEditorContext } from "./notes-editor-context";
import ConfirmationDeleteDialog from "./components/right-side-panel/components/rich-text-editor/components/dialogs/ConfirmationDeleteDialog";
import { SKIP_CONTENT_BOX_DELETE_CONFIRM_KEY } from "./constants/constants";

const debouncedUpdateNote = debounceWithMaxWait((func) => func(), 3000, 10000);
const debouncedFunction = debounce((func) => func(), 100);
const Notes = () => {
	const { call: getNote } = useApi(NotesService.getNote);
	const { call: updateNote } = useApi(NotesService.updateNote);
	const { call: updateNoteStatus } = useApi(NotesService.updateNoteStatus);
	const { call: getNoteDetails } = useApi(NotesService.getNoteDetails);
	const { call: getAllNotes } = useApi(NotesService.getAllNotes);
	const { call: getIsGenerating } = useApi(NotesService.getIsGenerating);
	const { call: getDocuments } = useApi(TransverseService.getDocuments);
	const noteId = useSelector(({ note }) => note.selectedNote?.id);
	const isLoading = useSelector(({ note }) => note.isLoading);
	const isGenerating = useSelector(({ note }) => note.isGenerating);
	const newlyGeneratedNote = useSelector(({ note }) => note.newlyGeneratedNote);
	const userId = useSelector(({ context }) => context.user?.id);
	const projectId = useSelector(({ context }) => context.project?.id);
	const selectedDocument = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.selectedDocument);
	const selectedNote = useSelector(({ note }) => note.selectedNote);
	const [noteStatus, setNoteStatus] = useState({ noteId: 0, userId: 0, status: "OPENED", lockedBy: "" });
	const noteContentRef = useRef({ type: "doc", content: [] });
	const noteStateRef = useRef({ noteId, userId });
	const [showToast, setShowToast] = useState(false);
	const [failureMessage, setFailureMessage] = useState(false);
	const [liveStatuses, setLiveStatuses] = useState({});
	const [isDisplayDocViewer, setIsDisplayDocViewer] = useState(false);
	const [isPanelOpen, setIsPanelOpen] = useState(true);
	const [isAtResponsiveBreakpoint, setIsAtResponsiveBreakpoint] = useState(false);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const editorRef = useRef(null);
	const sidePanelRef = useRef(null);
	const scrollTo = useSelector(({ notesPdf }) => notesPdf.scrollTo);
	const sseConnectionRef = useRef(null);
	const previousToast = usePrevious(showToast);
	const params = useParams();
	const dispatch = useDispatch();
	const trackEvent = useTrackEvent();
	const pendingTrRef = useRef(null);
	const hasDocument = useMemo(
		() => (!!selectedDocument.projectId && selectedDocument.id) || Number.parseInt(params?.docId, 10),
		[selectedDocument, params]
	);
	useEffect(() => {
		const breakpoint = isDisplayDocViewer && !!hasDocument ? 1440 : 1020;
		const mediaQueryList = window.matchMedia(`(max-width: ${breakpoint}px)`);
		const handleChange = (e) => {
			setIsPanelOpen(!e.matches);
			setIsAtResponsiveBreakpoint(e.matches);
		};
		handleChange(mediaQueryList);
		mediaQueryList.addEventListener("change", handleChange);
		return () => mediaQueryList.removeEventListener("change", handleChange);
	}, [isDisplayDocViewer, hasDocument]);
	useEffect(() => {
		if (!isAtResponsiveBreakpoint || !isPanelOpen) {
			return undefined;
		}
		const handleClickOutside = (e) => {
			if (sidePanelRef.current && !sidePanelRef.current.contains(e.target)) {
				setIsPanelOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isAtResponsiveBreakpoint, isPanelOpen]);
	useEffect(() => {
		if (scrollTo?.page !== undefined) {
			setIsDisplayDocViewer(true);
		}
	}, [scrollTo]);
	useEffect(() => {
		const documentTitle = translate("notes.document.title");
		trackEvent("Notes_visit");
		document.title = documentTitle;
	}, [trackEvent]);
	useEffect(() => {
		noteStateRef.current = { noteId, userId };
	}, [noteId, userId]);
	const handleSaveNote = (newContent) => {
		dispatch(setIsSaving(true));
		debouncedUpdateNote(() => {
			updateNote(
				{ noteId },
				{
					value: JSON.stringify(newContent),
				}
			)
				.then(() => {
					noteContentRef.current = newContent;
					dispatch(setIsSaving(false));
					trackEvent("Notes_Manual_Writing");
				})
				.catch(console.error);
		});
	};
	const handleFocus = () => {
		if (noteId && noteStatus.status === "OPENED") {
			updateNoteStatus({ noteId }, { status: "LOCKED" })
				.then(() => {
					setNoteStatus({ noteId, userId, status: "LOCKED" });
				})
				.catch(console.error);
		}
	};
	const handleUpdate = (newContent) => {
		if (noteId && !isLoading && !isDeepEqual(noteContentRef.current, newContent)) {
			handleSaveNote(newContent);
		}
	};

	const editor = useEditor({
		extensions: [
			StarterKit,
			ContentBoxExtention.configure({
				onDeleteAttempt: (tr) => {
					if (localStorage.getItem(SKIP_CONTENT_BOX_DELETE_CONFIRM_KEY) === "true") {
						tr.setMeta("contentBoxDeleteConfirmed", true);
						queueMicrotask(() => editorRef.current?.view.dispatch(tr));
						return;
					}
					pendingTrRef.current = tr;
					setConfirmDeleteOpen(true);
				},
			}),
			LineReferenceExtention,
		],
		content: "",
		editable: noteStatus.userId === userId || (noteStatus.status === "OPENED" && noteStatus.userId === noteId),
		onUpdate: ({ editor: updatedEditor }) => handleUpdate(updatedEditor.getJSON()),
		onFocus: () => handleFocus(),
		editorProps: {
			clipboardTextSerializer: (content) =>
				// Custom text serialization if needed
				content,
		},
	});
	useEffect(() => {
		editorRef.current = editor;
	}, [editor]);
	const handleGetFirstNote = useCallback(() => {
		getAllNotes({ projectId }, { page: 0, limit: 1, sort: "LAST_MODIFICATION", dir: "DESC" }, {})
			.then((data) => {
				if (isNonEmptyArray(data.contents)) {
					dispatch(
						setSelectedNote({
							id: data.contents[0].id,
							title: data.contents[0].title,
							generatedByAI: data.contents[0].generatedByAI,
							lastModified: data.contents[0].lastModified,
							elapsed: data.contents[0].elapsed,
							lastModifiedBy: data.contents[0].lastModifiedBy,
						})
					);
					if (data.contents[0].status === "OPENED") {
						updateNoteStatus({ noteId: data.contents[0].id }, { status: "LOCKED" })
							.then(() => {
								setNoteStatus({ noteId: data.contents[0].id, userId, status: "LOCKED" });
							})
							.catch(console.error);
					} else {
						setNoteStatus({
							noteId: data.contents[0].id,
							userId: data.contents[0].lastModifiedById,
							status: "LOCKED",
						});
					}
				}
			})
			.catch(console.error);
	}, [getAllNotes, projectId, updateNoteStatus, userId, dispatch]);
	const clearHistory = useCallback(() => {
		if (!editor) {
			return;
		}
		const { state, view } = editor;
		const newState = EditorState.create({
			doc: state.doc,
			plugins: state.plugins,
			schema: state.schema,
		});

		view.updateState(newState);
	}, [editor]);
	const handleGetNote = useCallback(() => {
		dispatch(setIsLoading(true));
		getNote({ noteId })
			.then((data) => {
				noteContentRef.current = data || { type: "doc", content: [] };
				editor.commands.setContent(data, { emitUpdate: false });
				debouncedFunction(() => {
					dispatch(setIsLoading(false));
					clearHistory();
				});
			})
			.catch(console.error);
	}, [getNote, noteId, editor, dispatch, clearHistory]);

	useEffect(() => {
		if (projectId) {
			getIsGenerating({ projectId })
				.then((data) => dispatch(setIsGenerating(data.value)))
				.catch(console.error);
		}
	}, [getIsGenerating, dispatch, projectId]);
	useEffect(() => {
		if (editor?.commands && noteId && !isLoading) {
			editor.commands.focus();
		}
	}, [editor, isLoading, noteId]);
	useEffect(() => {
		if (selectedDocument.projectId !== projectId) {
			getDocuments(
				{ projectId },
				{ page: 0, limit: 1 },
				{ separator: OPERATORS.AND, Type: "QuickAccessSearchFilterDTO" }
			)
				.then((data) => {
					if (isNonEmptyArray(data.contents)) {
						dispatch(
							setSelectedDocument({
								id: data.contents[0].id,
								name: data.contents[0].name,
								label: data.contents[0].documentFileName,
								projectId,
							})
						);
					}
				})
				.catch(console.error);
		}
	}, [selectedDocument, dispatch, getDocuments, projectId]);
	useEffect(() => {
		if (noteId === noteStatus.noteId) {
			if (noteStatus.userId !== userId) {
				handleGetNote();
			}
			if (noteStatus.userId === userId) {
				editor?.setEditable(true);
				dispatch(setIsEditable(true));
			} else {
				editor?.setEditable(noteStatus.status === "OPENED");
				dispatch(setIsEditable(noteStatus.status === "OPENED"));
			}
		}
	}, [handleGetNote, noteStatus, noteId, userId, editor, dispatch]);
	function handleStatusEvent({ noteIdSSE, reason, userIdSSE }) {
		setLiveStatuses((prev) => ({
			...prev,
			[noteIdSSE]: {
				noteId: noteIdSSE,
				status: reason,
				userId: userIdSSE,
			},
		}));
	}
	const handleLockMessage = useCallback(
		({ userId: userIdSSE, noteId: noteIdSSE, reason, lastModifiedBy }) => {
			const { noteId: noteIdRef, userId: userIdRef } = noteStateRef.current;
			handleStatusEvent({ noteIdSSE, reason, userIdSSE });
			if (noteIdRef === noteIdSSE && userIdSSE !== userIdRef) {
				setNoteStatus({ noteId: noteIdSSE, userId: userIdSSE, status: reason, lockedBy: lastModifiedBy });
				if (reason === "OPENED") {
					updateNoteStatus({ noteId: noteIdSSE }, { status: "LOCKED" }).catch(console.error);
				}
			}
		},
		[updateNoteStatus]
	);
	const handleSelectNote = useCallback(() => {
		getNoteDetails({ noteId: newlyGeneratedNote })
			.then((data) => {
				dispatch(setSelectedNote(data));
				dispatch(setNewlyGeneratedNote(0));
				if (data.status === "OPENED") {
					updateNoteStatus({ noteId: data.id }, { status: "LOCKED" })
						.then(() => {
							setNoteStatus({ noteId: data.id, userId, status: "LOCKED" });
						})
						.catch(console.error);
				} else {
					setNoteStatus({ noteId: data.id, userId: data.lastModifiedById, status: "LOCKED" });
				}
			})
			.catch(console.error);
	}, [dispatch, getNoteDetails, newlyGeneratedNote, userId, updateNoteStatus]);
	const handleAgentResponse = useCallback(
		({ reason, projectId: projectIdSSE, noteId: noteIdSSE }) => {
			const { noteId: noteIdRef } = noteStateRef.current;
			if (projectId === projectIdSSE) {
				if (reason === "CREATED") {
					if (!noteIdRef) {
						handleGetFirstNote();
					}
					dispatch(setIsGenerating(false));
					setShowToast(true);
					dispatch(setNewlyGeneratedNote(noteIdSSE));
				}
				if (reason === "FAILED") {
					setFailureMessage(true);
					dispatch(setIsGenerating(false));
				}
			}
		},
		[dispatch, projectId, handleGetFirstNote]
	);
	useEffect(() => {
		if (!showToast && previousToast) {
			handleSelectNote();
		}
	}, [previousToast, showToast, handleSelectNote]);
	useEffect(() => {
		if (!projectId) {
			return null;
		}
		sseConnectionRef.current?.();
		const onMessage = (message) => {
			const { data } = message;
			if (data.action === "STATUS") {
				handleLockMessage(data.payload);
			}
			if (data.action === "CREATE") {
				handleAgentResponse(data.payload);
			}
		};
		const onError = (error) => {
			console.error(error);
		};
		sseConnectionRef.current = connectSSE(`notes/projects/${projectId}/subscribe`, { onMessage, onError });
		return () => {
			sseConnectionRef.current?.();
		};
	}, [projectId, handleLockMessage, handleAgentResponse]);
	useEffect(() => {
		if (noteId) {
			handleGetNote();
		}
	}, [handleGetNote, editor, noteId]);
	useEffect(() => {
		if (noteId) {
			getNoteDetails({ noteId })
				.then((data) =>
					setNoteStatus({
						noteId: data.id,
						userId: data.lastModifiedById,
						status: data.status,
						lockedBy: data.lastModifiedBy,
					})
				)
				.catch(console.error);
		}
	}, [getNoteDetails, noteId]);
	useEffect(() => {
		if (!noteId) {
			handleGetFirstNote();
		}
	}, [handleGetFirstNote, noteId]);
	const handleDisplayDocViewer = useCallback(() => {
		setIsDisplayDocViewer((prev) => !prev);
	}, []);
	const handleConfirmDelete = () => {
		const tr = pendingTrRef.current;
		pendingTrRef.current = null;
		setConfirmDeleteOpen(false);
		if (!tr || !editor) {
			return;
		}
		if (!editor.state.doc.eq(tr.before)) {
			return;
		}
		tr.setMeta("contentBoxDeleteConfirmed", true);
		editor.view.dispatch(tr);
	};
	const handleCancelDelete = () => {
		pendingTrRef.current = null;
		setConfirmDeleteOpen(false);
	};
	const contextNotes = useMemo(
		() => ({ isDisplayDocViewer, onCloseDocViewer: handleDisplayDocViewer }),
		[isDisplayDocViewer, handleDisplayDocViewer]
	);

	return (
		<NotesEditorContext.Provider value={contextNotes}>
			{(isGenerating || failureMessage) && (
				<div className={styles.isGeneratingContainer} data-failure={failureMessage}>
					<IconComponent
						color={(isGenerating && "var(--blue-500)") || "var(--red-500)"}
						icon={icon[(isGenerating && "faSpinner") || "faTimesCircle"]}
						spin={isGenerating}
					/>
					<div className={styles.isGeneratingContainer__title}>
						{translate(
							(isGenerating && "notes.is-generating-banner.title") ||
								"notes.ai-generation.error-notification"
						)}
					</div>
					{failureMessage && (
						<CustomIconButton icon={icon.faTimes} onClick={() => setFailureMessage(false)} />
					)}
				</div>
			)}
			<div className={styles.container}>
				{isNonEmptyObject(selectedNote) && (
					<div
						ref={sidePanelRef}
						className={styles.sidePanelContainer}
						data-has-document={isDisplayDocViewer && !!hasDocument}
						data-open={isPanelOpen}
					>
						<NotesSidePanel
							isPanelOpen={isPanelOpen}
							liveStatuses={liveStatuses}
							noteStatus={noteStatus}
							onSetNoteStatus={setNoteStatus}
							onTogglePanel={() => setIsPanelOpen((prev) => !prev)}
						/>
					</div>
				)}
				<div
					className={styles.notesContainer}
					data-has-document={isDisplayDocViewer && !!hasDocument && isNonEmptyObject(selectedNote)}
				>
					<div className={styles.notesContainerTEMP}>
						<CentralPanel
							debouncedUpdateNote={debouncedUpdateNote}
							editor={editor}
							liveStatuses={liveStatuses}
							noteStatus={noteStatus}
							onSetNoteStatus={setNoteStatus}
						/>
					</div>
				</div>
				<div
					className={styles.pdfContainer}
					data-has-document={isDisplayDocViewer && !!hasDocument && isNonEmptyObject(selectedNote)}
				>
					{isNonEmptyObject(selectedNote) &&
						isDisplayDocViewer &&
						((hasDocument && (
							<DocumentViewer editor={editor} onCloseDocViewer={handleDisplayDocViewer} />
						)) || <EmptyState />)}
				</div>
			</div>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={4000}
				open={showToast}
				onClose={() => {
					setShowToast(false);
				}}
			>
				<Alert
					icon={<IconComponent color="currentColor" icon={icon.faCheckCircle} size="sm" />}
					severity="success"
					onClose={() => {
						setShowToast(false);
					}}
				>
					{translate("notes.is-newly-generated-banner.title")}
				</Alert>
			</Snackbar>
			<ConfirmationDeleteDialog
				open={confirmDeleteOpen}
				title="Hello"
				onClose={handleCancelDelete}
				onSubmit={handleConfirmDelete}
			/>
		</NotesEditorContext.Provider>
	);
};

export { default as notesDocumentsSlice } from "./slice/notes-documents/notes-documents-slice";
export { default as notesSelectedDocumentSlice } from "./slice/notes-selected-document/notes-selected-document-slice";
export { default as notesPdfSlice } from "./slice/notes-pdf/notes-pdf-slice";
export { default as noteSlice } from "./slice/note/note-slice";

export default exportView({
	path: [{ path: "/projects/:projectId/review/notes" }, { path: ":docId/*" }],
	localesPath: "/notes/locales",
	component: Notes,
	segFlag: SegFlags.AI_NOTE,
});
